/** Viewport box used by the spotlight and the callout. */
export interface GuideBox {
  top: number;
  left: number;
  width: number;
  height: number;
}

export type GuideCalloutPlace = 'right' | 'left' | 'below' | 'above';

export interface GuidePlacement {
  spot: GuideBox;
  callout: GuideBox;
  place: GuideCalloutPlace;
}

const GAP = 12;

function clamp(value: number, min: number, max: number): number {
  if (max < min) return min;
  return Math.min(max, Math.max(min, value));
}

function intersects(a: GuideBox, b: GuideBox): boolean {
  return !(
    a.left + a.width <= b.left ||
    b.left + b.width <= a.left ||
    a.top + a.height <= b.top ||
    b.top + b.height <= a.top
  );
}

/**
 * Frame stays on the target (clipped to the viewport). The callout sits beside it
 * and, on a narrow screen or a full-bleed target, above or below — still inside
 * the viewport and not on top of the frame.
 */
export function placeGuideCallout(
  target: { top: number; left: number; right: number; bottom: number },
  viewport: { width: number; height: number },
  calloutSize: { width: number; height: number },
): GuidePlacement {
  const margin = viewport.width < 640 ? 16 : 12;
  const pad = 6;
  const vw = viewport.width;
  const vh = viewport.height;
  const cw = Math.min(calloutSize.width, Math.max(160, vw - margin * 2));
  const ch = Math.min(calloutSize.height, Math.max(72, vh - margin * 2));
  const band = ch + GAP + margin;

  let top = clamp(target.top - pad, 0, vh);
  let left = clamp(target.left - pad, 0, vw);
  let right = clamp(target.right + pad, 0, vw);
  let bottom = clamp(target.bottom + pad, 0, vh);
  if (right < left) right = left;
  if (bottom < top) bottom = top;

  const room = () => ({
    right: vw - margin - right,
    left: left - margin,
    below: vh - margin - bottom,
    above: top - margin,
  });

  const sideOk = (space: number) => space >= cw + GAP;
  const blockOk = (space: number) => space >= ch + GAP;
  const anyFit = (r: ReturnType<typeof room>) =>
    sideOk(r.right) || sideOk(r.left) || blockOk(r.below) || blockOk(r.above);

  let r = room();
  if (!anyFit(r) && bottom - top > band + 64) {
    bottom = Math.min(bottom, vh - band);
    r = room();
  }
  if (!anyFit(r) && bottom - top > band + 64) {
    top = Math.max(top, band);
    r = room();
  }
  if (!anyFit(r)) {
    const maxBottom = vh - band;
    if (maxBottom - margin >= 64) {
      top = margin;
      bottom = Math.max(top + 64, Math.min(bottom, maxBottom));
    }
    r = room();
  }

  const spotW = Math.max(0, right - left);
  const preferVertical = vw < 720 && spotW > vw * 0.45;

  let place: GuideCalloutPlace;
  if (!preferVertical && sideOk(r.right)) place = 'right';
  else if (!preferVertical && sideOk(r.left)) place = 'left';
  else if (blockOk(r.below)) place = 'below';
  else if (blockOk(r.above)) place = 'above';
  else if (sideOk(r.right)) place = 'right';
  else if (sideOk(r.left)) place = 'left';
  else if (r.below >= r.above) place = 'below';
  else place = 'above';

  if ((place === 'below' || place === 'above') && vw >= 720 && spotW < vw * 0.55) {
    if (sideOk(r.right)) place = 'right';
    else if (sideOk(r.left)) place = 'left';
  }
  // Heading-sized targets sit better with the note underneath than over the actions beside them.
  if (place === 'right' || place === 'left') {
    if (blockOk(r.below) && spotW < Math.min(280, vw * 0.45)) place = 'below';
  }

  if (place === 'below' && !blockOk(room().below) && bottom - top > 64) {
    bottom = Math.max(top + 64, Math.min(bottom, vh - band));
    r = room();
    if (blockOk(r.below)) place = 'below';
  }
  if (place === 'above' && !blockOk(room().above) && bottom - top > 64) {
    top = Math.min(bottom - 64, Math.max(top, band));
    r = room();
  }

  let x = margin;
  let y = margin;
  if (place === 'right') {
    x = right + GAP;
    y = clamp(top, margin, vh - margin - ch);
  } else if (place === 'left') {
    x = left - GAP - cw;
    y = clamp(top, margin, vh - margin - ch);
  } else if (place === 'below') {
    y = bottom + GAP;
    x = clamp(left, margin, vw - margin - cw);
  } else {
    y = top - GAP - ch;
    x = clamp(left, margin, vw - margin - cw);
  }
  x = clamp(x, margin, Math.max(margin, vw - margin - cw));
  y = clamp(y, margin, Math.max(margin, vh - margin - ch));

  const spot: GuideBox = {
    top,
    left,
    width: Math.max(0, right - left),
    height: Math.max(0, bottom - top),
  };
  let callout: GuideBox = { top: y, left: x, width: cw, height: ch };

  if (intersects(callout, spot)) {
    const belowY = spot.top + spot.height + GAP;
    const aboveY = spot.top - GAP - ch;
    if (belowY + ch <= vh - margin) {
      callout = { ...callout, top: belowY };
      place = 'below';
    } else if (aboveY >= margin) {
      callout = { ...callout, top: aboveY };
      place = 'above';
    } else {
      const freeBottom = vh - band;
      spot.top = margin;
      spot.height = Math.max(64, freeBottom - margin);
      callout = {
        ...callout,
        top: clamp(spot.top + spot.height + GAP, margin, vh - margin - ch),
      };
      place = 'below';
    }
    callout = {
      ...callout,
      left: clamp(callout.left, margin, Math.max(margin, vw - margin - cw)),
    };
  }

  return { spot, callout, place };
}
